/**
 * Mirrors the `filters:` block of `winsight-server`'s `reports/spreading/report.yaml`, which is
 * authoritative: `from`/`to` and `factory` are required, `spreading_table` is multi-valued
 * (comma-joined in the URL, `IN (:name)` on the wire), and the rest are plain text.
 *
 * Eight filters, down from the eighteen concepts the four legacy screens offered between them.
 * Batch, size and part were dropped as query filters and stay reachable as in-table column filters
 * instead — see the report's design-notes.md.
 *
 * **The schema is built per factory**, because the report has no all-factories answer: bed, table
 * and work-group codes are only meaningful inside one factory, and the queries bind `= :factory`
 * with no null guard.
 */

import { format, subDays } from 'date-fns';

import { FACTORY_OPTIONS, type FactoryCode } from '@/lib/factories';
import { liveOptionsFor, type FilterOption, type FilterSchema } from '@/lib/filters';

/** Option values come from `report.yaml`'s `options:` block, never a hardcoded array (§5.4). */
const live = liveOptionsFor('spreading');

const ISO = 'yyyy-MM-dd';

/**
 * Period the report opens on when the URL carries none. A week back to yesterday: the spreading
 * fact carries roughly 1.5M rows a month, and today is still being recorded.
 */
export function defaultPeriod(today: Date = new Date()): { from: string; to: string } {
  return { from: format(subDays(today, 7), ISO), to: format(subDays(today, 1), ISO) };
}

/**
 * Main body against the other garment parts, on the spine's `ismain`. Two real states and an
 * "either" — a `select`, not a `switch`, because "both" is the normal reading.
 */
const MAIN_BODY_OPTIONS: readonly FilterOption[] = [
  { value: 'Y', label: 'Main body' },
  { value: 'N', label: 'Other parts' },
];

/**
 * Mirrors the `filters:` block of `spreading/report.yaml`.
 *
 * Nine filters, down from the eighteen concepts the four legacy screens offered between them.
 * Batch, size and part were dropped as query filters and are reachable as in-table column filters
 * instead — see the report's design-notes.md.
 *
 * **A function of the factory**, as on Sewing Efficiency: `factory` is required and single-valued
 * because bed, spreading table and work-group codes are only meaningful inside one factory, so
 * there is no all-factories answer to fall back on.
 */
export function spreadingFilters(factory: FactoryCode): FilterSchema {
  return [
    // Factory leads: it is required, every other list is scoped by it, and the codes below only
    // mean anything once it is chosen.
    {
      type: 'select',
      name: 'factory',
      label: 'Factory',
      required: true,
      options: FACTORY_OPTIONS,
      defaultValue: factory,
    },
    {
      type: 'date-range',
      name: 'period',
      label: 'Spreading date',
      required: true,
      fromParam: 'from',
      toParam: 'to',
      defaultValue: defaultPeriod,
    },
    // Bound to `bedid`, the full table code (CTBVN5G) — which is also what the grid shows and what
    // the Summary drill-through carries, so the three cannot disagree. The list is served from the
    // fact itself and narrowed to the chosen factory (`options:`/`scope:` in report.yaml).
    {
      type: 'multi-select',
      name: 'spreading_table',
      label: 'Cutting table',
      anyLabel: 'All tables',
      options: live('spreading_table'),
    },
    { type: 'text', name: 'b_no', label: 'B No' },
    { type: 'text', name: 'mo', label: 'MO' },
    { type: 'text', name: 'working_no', label: 'Working no' },
    {
      type: 'select',
      name: 'building',
      label: 'Building',
      anyLabel: 'All buildings',
      options: live('building'),
    },
    {
      type: 'select',
      name: 'is_main_body',
      label: 'Part type',
      anyLabel: 'Main body and other parts',
      options: MAIN_BODY_OPTIONS,
    },
  ];
}
